import { InfoCircleOutlined } from '@ant-design/icons';
import { Select, Tooltip } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { OpService } from '../../../../util/services/operation';
import { selectLocation } from '../../../../util/store/location/constants';
import { locationActions } from '../../../../util/store/location/reducer';
import { selectPermissions, selectTenantId } from '../../../../util/store/selectors';
import { getStore, useSelector } from '../../../../util/store/store';
import { PATHNAME } from '../../../../util/varibles/constants';
import { showErrorResponse } from '../../../../util/varibles/global';
import { TOperation2 } from '../../../../util/varibles/interface';
import { ACTION } from '../../../../util/varibles/permission';
import { opDetailActions } from '../../reducer';

interface IProps {
    operation: TOperation2
    vesselId: string
}

const getKey = (type: string | undefined, id: string) => JSON.stringify([type, id]);

const StartPoint: React.FC<IProps> = ({ operation, vesselId }) => {
    const dispatch = useDispatch();
    const tenantId = useSelector(selectTenantId);
    const [canUpdate] = useSelector(selectPermissions([ACTION.OP_DETAIL.UPDATE]));
    const locations = useSelector(selectLocation.allLocations);
    const loading = useSelector(selectLocation.loadingAllLocations);
    const editable = canUpdate && operation.tenant_id === tenantId;
    const startPoint = operation.start_point;
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editable || !startPoint?.id) return;
        const { loadingAllLocations, allLocationsLoadedAt } = getStore().location;
        if (!loadingAllLocations && (!allLocationsLoadedAt || Date.now() - allLocationsLoadedAt >= 5 * 60 * 1000)) {
            dispatch(locationActions.getAllLocationsRequest());
        }
    }, [dispatch, editable, startPoint?.id]);

    const options = useMemo(() => locations.map(location => ({
        label: location.name,
        value: getKey(location.field, location.id),
    })), [locations]);

    if (!startPoint?.id) return <Link
        className='link text-[15px] font-normal'
        to={`${PATHNAME.VESSEL}/${vesselId}`}
    >
        Configure cost info to have Σ cost for the operation.
    </Link>;

    const value = getKey(startPoint.type, startPoint.id);
    const availableOptions = options.some(option => option.value === value)
        ? options
        : [{ label: startPoint.name, value }, ...options];

    const handleChange = async (key: string) => {
        if (!editable || saving || key === value) return;
        const location = locations.find(location => getKey(location.field, location.id) === key);
        const type = location?.field;
        if (!location || (type !== 'poi' && type !== 'site' && type !== 'factory')) return;

        const start_point = { id: location.id, name: location.name, type };
        setSaving(true);
        try {
            const billings = await OpService.updateStartPoint({ id: operation.id, tenant_id: operation.tenant_id, start_point });
            const { data } = getStore().operationDetail;
            if (data.operation?.id === operation.id && data.operation.tenant_id === operation.tenant_id) {
                dispatch(opDetailActions.updateState({
                    data: { ...data, operation: { ...data.operation, start_point, billings } },
                }));
            }
        } catch (error) {
            showErrorResponse('Update start location failed', error);
        } finally {
            setSaving(false);
        }
    };

    return <div className='flex items-center gap-1 text-[15px] font-normal'>
        <span>From:</span>
        {editable ? <Select
            key={`${operation.id}-${value}`}
            aria-label='Operation start location'
            variant='borderless'
            className='min-w-[140px] [&_.ant-select-selector]:!px-0'
            value={value}
            onChange={handleChange}
            options={availableOptions}
            loading={loading || saving}
            disabled={saving}
            showSearch={{ optionFilterProp: 'label' }}
            popupMatchSelectWidth={false}
        /> : <span>{startPoint.name}</span>}
        <Tooltip title={<>
            Ocean Planner uses this location as the starting point to calculate the traveling time to the operation location.
            {' '}<Link className='link' to={`${PATHNAME.VESSEL}/${vesselId}`}>Configure cost info</Link>
        </>}>
            <InfoCircleOutlined className='text-[var(--colorSub)]' aria-label='Start location information' />
        </Tooltip>
    </div>;
};

export default StartPoint;
